import { useState, useEffect } from 'react';
import { Storage } from '../storage';
import Icon from './Icon';
import Modal from './Modal';

// ─── ADD TO COLLECTION MODAL ──────────────────────────────────────────────────
//
//  Props:
//    open          {boolean}
//    onClose       {function}
//    timelineId    {string}
//    timelineName  {string}
//    currentUser   {object}

const AddToCollectionModal = ({ open, onClose, timelineId, timelineName, currentUser }) => {
  const [collections, setCollections] = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [addingId,    setAddingId]    = useState(null);
  const [addedIds,    setAddedIds]    = useState(new Set());
  const [newTitle,    setNewTitle]    = useState('');
  const [creating,    setCreating]    = useState(false);
  const [error,       setError]       = useState('');

  useEffect(() => {
    if (!open || !currentUser) return;
    setLoading(true);
    setAddedIds(new Set());
    setError('');
    setNewTitle('');
    Storage.getCollections(currentUser.id)
      .then(data => setCollections(data.collections ?? []))
      .catch(() => setError('Could not load collections.'))
      .finally(() => setLoading(false));
  }, [open, currentUser]);

  const handleAdd = async (collId) => {
    if (addingId || addedIds.has(collId)) return;
    setAddingId(collId);
    try {
      await Storage.addToCollection(collId, timelineId);
      setAddedIds(prev => new Set([...prev, collId]));
    } catch (err) {
      if (err?.message?.toLowerCase().includes('already')) {
        setAddedIds(prev => new Set([...prev, collId]));
      }
    } finally {
      setAddingId(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || creating) return;
    setCreating(true);
    setError('');
    try {
      const { collection } = await Storage.createCollection({ title: newTitle.trim() });
      setCollections(prev => [{ ...collection, timelineCount: 0 }, ...prev]);
      setNewTitle('');
    } catch {
      setError('Could not create collection.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add to Collection" width={480}>
      {loading ? (
        <p style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Loading…</p>
      ) : (
        <>
          {timelineName && (
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
              Adding: <strong style={{ color: 'var(--text)' }}>{timelineName}</strong>
            </p>
          )}

          {collections.length === 0 && !error ? (
            <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>
              You don't have any collections yet. Create one below.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px' }}>
              {collections.map(coll => {
                const isAdded  = addedIds.has(coll.id);
                const isAdding = addingId === coll.id;
                return (
                  <li
                    key={coll.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px',
                      background: 'var(--bg2)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)', marginBottom: 8,
                    }}
                  >
                    <Icon name="layers" size={15} color="var(--accent)" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{
                        fontSize: 13, fontWeight: 600, color: 'var(--text)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {coll.title}
                      </p>
                      <p style={{ fontSize: 11, color: 'var(--text3)' }}>
                        {coll.timelineCount ?? 0} timeline{(coll.timelineCount ?? 0) !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => handleAdd(coll.id)}
                      disabled={isAdding || isAdded}
                      aria-label={isAdded ? `${coll.title}: added` : `Add to ${coll.title}`}
                      style={{
                        flexShrink: 0,
                        display: 'flex', alignItems: 'center', gap: 5,
                        background: isAdded
                          ? 'color-mix(in srgb, var(--success) 12%, transparent)'
                          : 'var(--bg3)',
                        border: `1px solid ${isAdded
                          ? 'color-mix(in srgb, var(--success) 35%, transparent)'
                          : 'var(--border2)'}`,
                        borderRadius: 8, padding: '5px 12px',
                        color: isAdded ? 'var(--success)' : 'var(--text2)',
                        fontSize: 12, cursor: isAdded ? 'default' : 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {isAdding
                        ? '…'
                        : isAdded
                          ? <><Icon name="check" size={13} /> Added</>
                          : <><Icon name="plus" size={13} /> Add</>
                      }
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {error && (
            <p role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>{error}</p>
          )}

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            <p style={{
              fontSize: 12, fontWeight: 600, color: 'var(--text3)',
              textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10,
            }}>
              New Collection
            </p>
            <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8 }}>
              <input
                value={newTitle}
                onChange={e => setNewTitle(e.target.value.slice(0, 200))}
                placeholder="Collection name…"
                style={{ flex: 1 }}
                aria-label="New collection name"
              />
              <button
                type="submit"
                className="btn-primary btn-sm"
                disabled={!newTitle.trim() || creating}
                style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
              >
                <Icon name="plus" size={13} />
                {creating ? 'Creating…' : 'Create'}
              </button>
            </form>
          </div>
        </>
      )}
    </Modal>
  );
};

export default AddToCollectionModal;
